/**
 * Menu d’actions de modération sur un message (chat live / log).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { BACKEND_URL } from '../../config';

export interface ModTargetMessage {
  pseudo: string;
  roomId: string;
  messageId?: string;
  text?: string;
}

interface ChatModActionsProps {
  staffPseudo: string;
  message: ModTargetMessage;
  onDone?: (toast: string) => void;
  align?: 'start' | 'end';
}

async function modPost(path: string, staffPseudo: string, body: unknown): Promise<Response> {
  const u = new URL(`${BACKEND_URL}${path}`, window.location.origin);
  u.searchParams.set('pseudo', staffPseudo);
  return fetch(u.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Pseudo': staffPseudo,
      'X-Moderator-Pseudo': staffPseudo,
    },
    body: JSON.stringify(body),
  });
}

export default function ChatModActions({
  staffPseudo,
  message,
  onDone,
  align = 'end',
}: ChatModActionsProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const run = useCallback(async (label: string, fn: () => Promise<Response>) => {
    setOpen(false);
    try {
      const res = await fn();
      onDone?.(res.ok ? label : `Échec (${res.status})`);
    } catch {
      onDone?.('Erreur réseau');
    }
  }, [onDone]);

  const roomId = message.roomId;
  const scope = roomId === 'global:compet' || roomId === 'global:detente'
    ? roomId
    : 'global:*';

  return (
    <div ref={rootRef} className={`chat-mod-actions align-${align}`}>
      <button
        type="button"
        className="message-action-btn chat-mod-trigger"
        title="Modérer"
        aria-label="Modérer"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        ⚖
      </button>
      {open && (
        <div className="chat-mod-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            onClick={() => void run(`Mute 10 min → ${message.pseudo}`, () =>
              modPost('/api/moderation/sanctions', staffPseudo, {
                target: message.pseudo,
                type: 'mute',
                scope,
                durationMinutes: 10,
                reason: 'Mute depuis le chat',
                evidenceMessageId: message.messageId,
              }))}
          >
            Mute 10 min
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => void run(`Mute 1 h → ${message.pseudo}`, () =>
              modPost('/api/moderation/sanctions', staffPseudo, {
                target: message.pseudo,
                type: 'mute',
                scope,
                durationMinutes: 60,
                reason: 'Mute depuis le chat',
                evidenceMessageId: message.messageId,
              }))}
          >
            Mute 1 h
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => void run(`Ban canal 7 j → ${message.pseudo}`, () =>
              modPost('/api/moderation/sanctions', staffPseudo, {
                target: message.pseudo,
                type: 'channel_ban',
                scope,
                durationMinutes: 60 * 24 * 7,
                reason: 'Ban canal depuis le chat',
                evidenceMessageId: message.messageId,
              }))}
          >
            Ban canal 7 j
          </button>
          {message.messageId && (
            <button
              type="button"
              role="menuitem"
              onClick={() => void run('Message effacé', () =>
                modPost('/api/moderation/messages/redact', staffPseudo, {
                  roomId,
                  messageId: message.messageId,
                  reason: 'Modération depuis le chat',
                }))}
            >
              Effacer le message
            </button>
          )}
        </div>
      )}
    </div>
  );
}
